import { rebootMemorySystem } from "../systems/memory.js";
import { hideLightingOverlay, showLightingOverlay } from "../systems/lighting.js";
import { ensureCrosshairOverlay, showCrosshairAt } from "../ui/crosshair.js";
import { resetGameHud } from "../ui/gameHud.js";
import { playBootDiagBeep } from "../systems/audio.js";

const BOOT_BASELINE_HOLD = 2.4;
const BOOT_HEARTBEAT_SECONDS = 1.85;
const BOOT_AFTER_HEARTBEAT_HOLD = 0.55;
const RESPAWN_BOOT_BASELINE_HOLD = 0.2;
const RESPAWN_BOOT_HEARTBEAT_SECONDS = 0.6;
const RESPAWN_BOOT_AFTER_HEARTBEAT_HOLD = 0.2;
const BOOT_GRAPH_Z = 5;
const BOOT_DIAG_Z = 4;
const BOOT_LINE_GREEN = [0, 255, 180];
const BOOT_DIAG_LINES = 6;
const BOOT_DIAG_LINE_H = 14;
const BOOT_DIAG_EMIT_INTERVAL = 0.2; // seconds between new lines
const BOOT_DIAG_SCROLL_PX_PER_SEC = 90;

const DIAGNOSTIC_POOL = [
    "CHK RAM_BANK_0 .............. OK",
    "CHK RAM_BANK_1 .............. MISSING",
    "CHK RAM_BANK_2 .............. MISSING",
    "MOUNT /dev/optic ............ WAIT",
    "MOUNT /dev/sniff ............ OFFLINE",
    "KERNEL_SIG .................. PARTIAL",
    "OWNER_LINK .................. UNKNOWN",
    "SENSOR_BUS .................. DEGRADED",
    "PWR_RAIL_A .................. STABLE",
    "PWR_RAIL_B .................. FLICKER",
    "HEAP_SCAN ................... 0x3F2A..",
    "MODULE_GATE bite ............ ABSENT",
    "MODULE_GATE bark ............ ABSENT",
    "MODULE_GATE dig ............. ABSENT",
    "VISION_CORE ................. QUEUED",
    "WALK_CTRL ................... QUEUED",
    "AUDIO_OUT ................... MUTE",
    "NET_UPLINK .................. DENIED",
    "SAFE_MODE ................... ENGAGED",
    "REBOOT_SEQ .................. RUNNING",
    "CHKPOINT .................... NULL",
    "SELF_TEST ................... LOOP",
];

function getBootLayout() {
    const graphW = Math.min(220, Math.max(150, width() - 120));
    const graphH = 52;
    const labelW = 54;
    const totalW = labelW + 10 + graphW;
    const graphY = Math.round(center().y + 28);
    const labelX = Math.round(center().x - totalW / 2);
    const bannerY = Math.round(center().y - 110);
    const diagH = BOOT_DIAG_LINES * BOOT_DIAG_LINE_H;
    const diagY = bannerY + 36;

    return {
        graphW,
        graphH,
        labelX,
        labelY: graphY + 17,
        graphX: labelX + labelW + 10,
        graphY,
        bannerY,
        promptY: graphY + graphH + 48,
        diagX: Math.round(center().x - 160),
        diagY,
        diagW: 320,
        diagH,
    };
}

function getBootCpuPulseSamples() {
    return [
        0.5, 0.5, 0.44, 0.5,
        0.02, 1.0, 0.0, 0.72,
        0.5, 0.5, 0.5,
    ];
}

function drawGraphSegment(prev, cur, lineColor) {
    const delta = cur.sub(prev);
    const length = delta.len();
    if (length <= 0) return;

    add([
        rect(length + 1, 2),
        pos(prev.add(delta.scale(0.5))),
        anchor("center"),
        rotate(delta.angle()),
        color(lineColor[0], lineColor[1], lineColor[2]),
        fixed(),
        z(BOOT_GRAPH_Z + 2),
        "boot_cpu_graph",
    ]);
}

/**
 * @param {number} pulseProgress 0 = heartbeat still off-screen to the right;
 *   1 = fully scrolled across. Always draws in green.
 */
function renderBootCpuGraph(pulseProgress = 0, offset = vec2(0, 0)) {
    destroyAll("boot_cpu_graph");

    const layout = getBootLayout();
    const lineColor = BOOT_LINE_GREEN;
    const graphX = layout.graphX + offset.x;
    const graphY = layout.graphY + offset.y;
    const chartX = graphX + 8;
    const chartY = graphY + 8;
    const chartW = layout.graphW - 16;
    const chartH = layout.graphH - 16;

    add([
        text("CPU", { font: "ShareTechMono", size: 14 }),
        pos(layout.labelX + offset.x, layout.labelY + offset.y),
        color(100, 180, 210),
        fixed(),
        z(BOOT_GRAPH_Z),
        "boot_cpu_graph",
    ]);

    for (let gx = 1; gx < 5; gx++) {
        add([
            rect(1, chartH),
            pos(chartX + gx * Math.floor(chartW / 5), chartY),
            color(20, 28, 34),
            fixed(),
            z(BOOT_GRAPH_Z + 1),
            "boot_cpu_graph",
        ]);
    }

    const midY = chartY + Math.round(chartH * 0.5);

    // Dead flatline only — once the heartbeat exists, draw baseline in the gaps
    // around it so it doesn't sit underneath the spike.
    if (pulseProgress <= 0) {
        drawGraphSegment(vec2(chartX, midY), vec2(chartX + chartW, midY), lineColor);
        return;
    }

    const samples = getBootCpuPulseSamples();
    const pulseW = Math.round(chartW * 0.58);
    const step = pulseW / (samples.length - 1);
    const travel = chartW + pulseW;
    const pulseStartX = chartX + chartW + 2 - pulseProgress * travel;
    const pulseEndX = pulseStartX + pulseW;
    const chartRight = chartX + chartW;

    if (pulseStartX > chartX) {
        drawGraphSegment(
            vec2(chartX, midY),
            vec2(Math.min(pulseStartX, chartRight), midY),
            lineColor,
        );
    }

    const points = samples.map((value, i) => ({
        x: pulseStartX + i * step,
        y: chartY + Math.round((1 - value) * chartH),
    }));

    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const cur = points[i];
        if (cur.x < chartX || prev.x > chartRight) continue;
        const a = {
            x: Math.max(prev.x, chartX),
            y: prev.y,
        };
        const b = {
            x: Math.min(cur.x, chartRight),
            y: cur.y,
        };
        if (b.x <= a.x) continue;
        // Lerp Y if we clipped an endpoint to the chart edge
        if (a.x !== prev.x || b.x !== cur.x) {
            const t0 = (a.x - prev.x) / Math.max(0.0001, cur.x - prev.x);
            const t1 = (b.x - prev.x) / Math.max(0.0001, cur.x - prev.x);
            a.y = Math.round(prev.y + (cur.y - prev.y) * t0);
            b.y = Math.round(prev.y + (cur.y - prev.y) * t1);
        }
        drawGraphSegment(vec2(a.x, a.y), vec2(b.x, b.y), lineColor);
    }

    if (pulseEndX < chartRight) {
        drawGraphSegment(
            vec2(Math.max(pulseEndX, chartX), midY),
            vec2(chartRight, midY),
            lineColor,
        );
    }
}

function createDiagnosticFeed() {
    // Starts empty: lines land at the top and fill downward. Once the window
    // is full, new lines append at the bottom and the view auto-scrolls up —
    // classic terminal behaviour, clipped to the readout box.
    const history = [];
    const labels = [];
    let nextIndex = 0;
    let emitTimer = 0;
    let scroll = 0;
    let targetScroll = 0;
    let beepStep = 0;

    const POOL_EXTRA = 4; // recycle a few labels above/below the clip

    function ensureLabelPool(count) {
        while (labels.length < count) {
            labels.push(add([
                text("", { font: "ShareTechMono", size: 11 }),
                pos(0, -999),
                color(40, 120, 90),
                opacity(0),
                fixed(),
                z(BOOT_DIAG_Z),
                "boot_diag",
            ]));
        }
    }

    function emitLine() {
        history.push(DIAGNOSTIC_POOL[nextIndex % DIAGNOSTIC_POOL.length]);
        nextIndex += 1 + Math.floor(Math.random() * 2);
        playBootDiagBeep(beepStep);
        beepStep += 1;
        // Trim far-above history so the pool stays bounded
        const maxKeep = BOOT_DIAG_LINES + 12;
        if (history.length > maxKeep) {
            const drop = history.length - maxKeep;
            history.splice(0, drop);
            scroll = Math.max(0, scroll - drop * BOOT_DIAG_LINE_H);
            targetScroll = Math.max(0, targetScroll - drop * BOOT_DIAG_LINE_H);
        }
        const contentH = history.length * BOOT_DIAG_LINE_H;
        const viewH = getBootLayout().diagH;
        targetScroll = Math.max(0, contentH - viewH);
    }

    function paintFeed() {
        const L = getBootLayout();
        const top = L.diagY;
        const bottom = L.diagY + L.diagH;
        // First history index that could still be on-screen (with slack)
        const first = Math.max(0, Math.floor(scroll / BOOT_DIAG_LINE_H) - 1);
        const visibleCount = BOOT_DIAG_LINES + POOL_EXTRA;
        ensureLabelPool(visibleCount);

        for (let i = 0; i < labels.length; i++) {
            const lab = labels[i];
            if (!lab?.exists?.()) continue;
            const histIdx = first + i;
            if (histIdx >= history.length) {
                lab.opacity = 0;
                lab.pos = vec2(L.diagX, -999);
                continue;
            }
            const y = top + histIdx * BOOT_DIAG_LINE_H - scroll;
            lab.text = history[histIdx];
            lab.pos = vec2(L.diagX, y);

            // Hard clip to the container — fade only at the very edges
            if (y < top - 2 || y > bottom - BOOT_DIAG_LINE_H + 2) {
                lab.opacity = 0;
            } else if (y < top + 4) {
                lab.opacity = 0.35;
            } else if (y > bottom - BOOT_DIAG_LINE_H - 4) {
                lab.opacity = 0.45;
            } else {
                lab.opacity = 0.85;
            }
        }
    }

    // Clip shrouds so lines don't read through above/below the window
    function placeShrouds() {
        destroyAll("boot_diag_shroud");
        const L = getBootLayout();
        add([
            rect(L.diagW, 18),
            pos(L.diagX, L.diagY - 18),
            color(0, 0, 0),
            opacity(1),
            fixed(),
            z(BOOT_DIAG_Z + 2),
            "boot_diag",
            "boot_diag_shroud",
        ]);
        add([
            rect(L.diagW, 22),
            pos(L.diagX, L.diagY + L.diagH),
            color(0, 0, 0),
            opacity(1),
            fixed(),
            z(BOOT_DIAG_Z + 2),
            "boot_diag",
            "boot_diag_shroud",
        ]);
    }

    placeShrouds();
    paintFeed();

    const updater = onUpdate(() => {
        emitTimer += dt();
        while (emitTimer >= BOOT_DIAG_EMIT_INTERVAL) {
            emitTimer -= BOOT_DIAG_EMIT_INTERVAL;
            emitLine();
        }

        // Ease scroll toward the bottom of the buffer (terminal follow)
        if (scroll < targetScroll) {
            scroll = Math.min(
                targetScroll,
                scroll + BOOT_DIAG_SCROLL_PX_PER_SEC * dt(),
            );
        }

        paintFeed();
    });

    return {
        layoutFeed() {
            placeShrouds();
            paintFeed();
        },
        cancel() {
            updater.cancel();
            destroyAll("boot_diag");
        },
    };
}

export function registerBootScene() {
    scene("boot", (args = {}) => {
        hideLightingOverlay();
        resetGameHud();
        const canvas = document.querySelector("canvas");
        if (canvas) canvas.style.cursor = "none";
        ensureCrosshairOverlay();

        let bootGraphPulseProgress = 0;

        const bg = add([
            rect(width(), height()),
            pos(0, 0),
            color(0, 0, 0),
            fixed(),
        ]);

        const banner = add([
            text("SYSTEM REBOOT", { font: "ShareTechMono", size: 22 }),
            pos(center().x, getBootLayout().bannerY),
            anchor("center"),
            color(0, 255, 180),
            fixed(),
        ]);

        const prompt = add([
            text(args.autoStart ? "" : "Press SPACE or Click to Start", {
                font: "ShareTechMono",
                size: 16,
            }),
            pos(center().x, getBootLayout().promptY),
            anchor("center"),
            color(255, 200, 100),
            fixed(),
        ]);

        const diagFeed = createDiagnosticFeed();
        renderBootCpuGraph(0);

        onResize(() => {
            const layout = getBootLayout();
            bg.width = width();
            bg.height = height();
            banner.pos = vec2(center().x, layout.bannerY);
            prompt.pos = vec2(center().x, layout.promptY);
            diagFeed.layoutFeed();
            renderBootCpuGraph(bootGraphPulseProgress);
        });

        onUpdate(() => {
            showCrosshairAt(mousePos());
        });

        let isStarting = false;
        const bootTiming = args.respawnFromCheckpoint
            ? {
                baselineHold: RESPAWN_BOOT_BASELINE_HOLD,
                heartbeatSeconds: RESPAWN_BOOT_HEARTBEAT_SECONDS,
                afterHeartbeatHold: RESPAWN_BOOT_AFTER_HEARTBEAT_HOLD,
            }
            : {
                baselineHold: BOOT_BASELINE_HOLD,
                heartbeatSeconds: BOOT_HEARTBEAT_SECONDS,
                afterHeartbeatHold: BOOT_AFTER_HEARTBEAT_HOLD,
            };

        function finishToGame() {
            diagFeed.cancel();
            showLightingOverlay();
            if (args.resetMemory !== false) {
                rebootMemorySystem({ kernelOnly: !!args.kernelOnlyMemory });
            }
            go("game", {
                openMemoryOnInit: args.openMemoryOnInit === true,
                memoryTutorialLock: !!args.kernelOnlyMemory,
                respawnFromCheckpoint: !!args.respawnFromCheckpoint,
            });
        }

        function playHeartbeatThenContinue() {
            // Dead flatline longer, then heartbeat slowly enters from the right.
            wait(bootTiming.baselineHold, () => {
                bootGraphPulseProgress = 0;
                const sweep = onUpdate(() => {
                    bootGraphPulseProgress = Math.min(
                        1,
                        bootGraphPulseProgress + dt() / bootTiming.heartbeatSeconds,
                    );
                    renderBootCpuGraph(bootGraphPulseProgress);
                    if (bootGraphPulseProgress >= 1) {
                        sweep.cancel();
                        wait(bootTiming.afterHeartbeatHold, finishToGame);
                    }
                });
            });
        }

        function startSystem() {
            if (isStarting) return;
            isStarting = true;
            prompt.text = "";
            playHeartbeatThenContinue();
        }

        if (args.autoStart) {
            startSystem();
        } else {
            onKeyPress("space", startSystem);
            // α.26+: bare onClick(fn) removed — use onMousePress
            onMousePress(startSystem);
        }
    });
}
